import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, Text, View } from 'react-native';
import { useQueryClient } from '@tanstack/react-query';
import { StyleSheet, useUnistyles } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import PaymentWebView from '@/components/Giving/PaymentWebView';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    createPaymentMethodConfig,
    createPaymentMethodConfigUnauthenticated,
    finalizePaymentMethodConfig,
    finalizePaymentMethodConfigUnauthenticated,
    getPaymentMethodConfigUrl,
} from '@/data/giving';

const PAYMENT_METHOD_FIELDS = [
    'paymentMethodID',
    'methodType',
    'cardBrand',
    'cardLast4',
    'cardExpMonth',
    'cardExpYear',
    'achAccountNumberLast4',
    'achBankName',
    'achAccountType',
];

const sanitizePaymentMethod = (method) => {
    if (!method || typeof method !== 'object') return null;
    const sanitized = {};
    PAYMENT_METHOD_FIELDS.forEach((field) => {
        if (method[field] !== undefined && method[field] !== null) sanitized[field] = method[field];
    });
    return sanitized.paymentMethodID ? sanitized : null;
};

const extractPaymentMethodID = (detail) => {
    if (!detail) return null;
    if (typeof detail === 'string') {
        try {
            return extractPaymentMethodID(JSON.parse(detail));
        } catch {
            return null;
        }
    }
    if (typeof detail !== 'object') return null;
    const direct = detail.payment_method_id || detail.paymentMethodID || detail.paymentMethodId;
    if (typeof direct === 'string' && direct) return direct;
    const method = detail.payment_method || detail.paymentMethod;
    const methodID = method?.id || method?.payment_method_id || method?.paymentMethodID || method?.paymentMethodId;
    if (typeof methodID === 'string' && methodID) return methodID;
    for (const value of Object.values(detail)) {
        const nested = extractPaymentMethodID(value);
        if (nested) return nested;
    }
    return null;
};

export default function AddPaymentMethodModal({
    isOpen,
    onClose,
    onPaymentMethodAdded,
    organizationID,
    personID,
    orgHandle,
    billingContact,
    authenticated = true,
}) {
    const queryClient = useQueryClient();
    const { theme: { colors, scheme } } = useUnistyles();
    const [config, setConfig] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [finalizing, setFinalizing] = useState(false);
    const requestGeneration = useRef(0);
    const finalizingRef = useRef(false);
    const isOpenRef = useRef(isOpen);

    const identity = useMemo(() => authenticated
        ? { organizationID, ...(personID ? { personID } : {}), ...(billingContact ? { billingContact } : {}) }
        : { orgHandle, ...(billingContact ? { billingContact } : {}) },
    [authenticated, organizationID, personID, orgHandle, billingContact]);

    useEffect(() => {
        isOpenRef.current = isOpen;
        if (!isOpen) {
            requestGeneration.current += 1;
            setConfig(null);
            setError(null);
            setLoading(false);
            setFinalizing(false);
            return;
        }

        const generation = ++requestGeneration.current;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const result = authenticated
                    ? await createPaymentMethodConfig(identity)
                    : await createPaymentMethodConfigUnauthenticated(identity);
                if (requestGeneration.current === generation) setConfig(result);
            } catch (err) {
                if (requestGeneration.current === generation) setError(err?.message || 'Failed to load payment method form');
            } finally {
                if (requestGeneration.current === generation) setLoading(false);
            }
        };
        load();
    }, [isOpen, authenticated, identity]);

    const paymentUrl = useMemo(() => {
        if (!config?.sessionKey || !config?.paymentMethodConfigID) return null;
        return getPaymentMethodConfigUrl({
            sessionKey: config.sessionKey,
            paymentMethodConfigID: config.paymentMethodConfigID,
            theme: scheme === 'dark' ? 'dark' : 'light',
            bg: colors.background,
        });
    }, [config, scheme, colors.background]);

    const handleClose = useCallback(() => {
        if (finalizingRef.current) return;
        onClose?.();
    }, [onClose]);

    const handleApproved = useCallback(async (detail) => {
        if (finalizingRef.current) return;
        const paymentMethodID = extractPaymentMethodID(detail);
        if (!paymentMethodID) {
            if (isOpenRef.current) setError('Payment details were stored, but the payment method could not be identified. Close and try again.');
            return;
        }

        finalizingRef.current = true;
        setFinalizing(true);
        setError(null);
        try {
            const payload = {
                ...identity,
                paymentMethodConfigID: config.paymentMethodConfigID,
                paymentMethodID,
            };
            const result = authenticated
                ? await finalizePaymentMethodConfig(payload)
                : await finalizePaymentMethodConfigUnauthenticated(payload);
            const method = sanitizePaymentMethod(result?.paymentMethod);
            if (!method) throw new Error('Saved payment method details were incomplete');

            if (authenticated && organizationID && personID) {
                const queryKey = QueryKeys.giving.paymentMethods(organizationID, personID);
                queryClient.setQueryData(queryKey, (current = []) => [
                    method,
                    ...current.filter((item) => (item.paymentMethodID || item.id) !== method.paymentMethodID),
                ]);
                queryClient.invalidateQueries({ queryKey });
            }
            await onPaymentMethodAdded?.(method);
            finalizingRef.current = false;
            if (isOpenRef.current) setFinalizing(false);
            handleClose();
        } catch (err) {
            finalizingRef.current = false;
            if (isOpenRef.current) {
                setError(err?.message || 'Failed to finish saving payment method');
                setFinalizing(false);
            }
        }
    }, [authenticated, config, identity, handleClose, onPaymentMethodAdded, organizationID, personID, queryClient]);

    return (
        <SheetModal
            isOpen={isOpen}
            onClose={handleClose}
            title="Add a payment method"
            subtitle="Card or bank account"
            loading={loading || finalizing}
        >
            <View style={styles.container} testID="add-payment-method-modal">
                {(loading || finalizing) && (
                    <View style={styles.status} testID="add-payment-method-loading">
                        <ActivityIndicator color={colors.giving} />
                        <Text style={styles.statusText}>{finalizing ? 'Saving payment method...' : 'Loading secure form...'}</Text>
                    </View>
                )}
                {error && <Text style={styles.error} testID="add-payment-method-error">{error}</Text>}
                {paymentUrl && !finalizing && (
                    <PaymentWebView
                        url={paymentUrl}
                        onSuccess={handleApproved}
                        onError={(message) => {
                            if (isOpenRef.current) setError(message || 'Payment method could not be saved');
                        }}
                        style={styles.webview}
                    />
                )}
            </View>
        </SheetModal>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing, typography }) => ({
    container: {
        minHeight: 520,
        padding: spacing.sm,
        gap: spacing.sm,
    },
    webview: {
        minHeight: 520,
    },
    status: {
        minHeight: 120,
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.sm,
    },
    statusText: {
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center',
    },
    error: {
        padding: spacing.sm,
        fontSize: typography.body,
        fontFamily: fonts.body,
        color: colors.error,
        backgroundColor: colors.errorContainer,
        textAlign: 'center',
    },
}));
